import React, { useState } from 'react';
import { Grid, Typography, Paper, Box } from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Page, Content } from '@backstage/core-components';
import { IssuesListComponent } from '../IssuesListComponent/IssuesListComponent';
import { IssueDetailComponent } from '../IssueDetailComponent/IssueDetailComponent';
import { useUptimeErrorsEntity } from '../../hooks/useUptimeMonitorEntity';
import { useIssues } from '../../hooks/useIssues';
import { useIssueDetail } from '../../hooks/useIssueDetail';
import { Issue } from '../../types';

export const IssuesWrapperComponent = () => {
  const { entity } = useEntity();
  const entityResult = useUptimeErrorsEntity(entity);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  // Handle entity parsing errors
  if (!entityResult.success) {
    return (
      <Page themeId="tool">
        <Content>
          <Paper style={{ padding: 16 }}>
            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
              <Typography variant="h6" color="error" gutterBottom>
                Configuration Error
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                {entityResult.error}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Please check your entity annotations and try again.
              </Typography>
            </Box>
          </Paper>
        </Content>
      </Page>
    );
  }

  const { host, org, projectId } = entityResult.data;

  const {
    data: issues,
    isLoading,
    error,
    currentFilters,
    handleFilterChange,
    handleSearch,
    handlePageChange,
    refetch,
  } = useIssues({ host, org, projectId });

  const {
    data: issueDetailData,
    isLoading: isDetailLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useIssueDetail(
    selectedIssue ? { host, id: selectedIssue.id } : null
  );

  const handleIssueClick = (issue: Issue) => {
    setSelectedIssue(issue);
    setDetailDrawerOpen(true);
  };

  const handleDetailClose = () => {
    setDetailDrawerOpen(false);
    setSelectedIssue(null);
  };

  const handleDetailUpdate = () => {
    refetch();
    refetchDetail();
  };

  return (
    <Page themeId="tool">
      <Content>
        <Grid container spacing={3} direction="column">
          <Grid item>
            <IssuesListComponent
              issues={issues}
              isLoading={isLoading}
              error={error}
              currentFilters={currentFilters}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onRefresh={refetch}
              onIssueClick={handleIssueClick}
              onPageChange={handlePageChange}
              host={host}
              org={org}
              projectId={projectId}
            />
          </Grid>
        </Grid>

        {/* Issue Detail Dialog */}
        <IssueDetailComponent
          data={issueDetailData}
          isLoading={isDetailLoading}
          error={detailError}
          open={detailDrawerOpen}
          onClose={handleDetailClose}
          onUpdate={handleDetailUpdate}
          host={host}
          org={org}
        />
      </Content>
    </Page>
  );
};
